
import React, { useState, useEffect } from "react";
import {
  Calendar,
  CreditCard,
  FileText,
  CheckCircle,
  Sun,
  Cloud,
  CloudRain,
} from "lucide-react";
import RoofBidderHeader from "../RoofBidderHeader";
import { supabase, supabaseHelpers } from "../../lib/supabase";
import { getJob, getBidsByJob, getInstallerProfile } from "../../lib/data";
import { useLocation, useNavigate } from "react-router-dom";
import { generatePrintableContent, generatePDF } from "../../utils/pdfGenerator";

import ScheduleStep from "../../components/ConfirmInstallation/ScheduleStep";
import DetailsStep from "../../components/ConfirmInstallation/DetailsStep";
import PaymentMethodStep from "../../components/ConfirmInstallation/PaymentMethodStep";
import ReviewStep from "../../components/ConfirmInstallation/ReviewStep";
import PaymentProcessingStep from "../../components/ConfirmInstallation/PaymentProcessingStep";
import ConfirmationView from "../../components/ConfirmInstallation/ConfirmationView";

const ConfirmInstallationPage = ({ onClose }) => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const bidIdParam = urlParams.get("bidId");
  const jobIdParam = urlParams.get("jobId");
  const demoMode = urlParams.get("demo") === "true";

  console.log("BID:", bidIdParam);

  const [currentStep, setCurrentStep] = useState(demoMode ? 6 : 1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentCardDetails, setPaymentCardDetails] = useState({
    cardNumber: "4242 4242 4242 4242",
    expiryDate: "12/25",
    cvv: "123",
    cardholderName: "John Smith",
    billingAddress: "10349 Courageous Drive, Indianapolis, IN 46236",
  });
  const [installationData, setInstallationData] = useState({
    preferredDate: demoMode ? "2025-12-25" : "",
    preferredTime: demoMode ? "Morning (8AM - 12PM)" : "",
    alternateDate: "",
    alternateTime: "",
    contactMethod: "phone",
    specialInstructions: "",
    accessInstructions: "",
    emergencyContact: "",
    paymentMethod: "payment-plan",
  });

  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  // Mock homeowner details - in real app, this would come from user session or localStorage
  const homeownerDetails = {
    name: "John Smith",
    email: "john.smith@example.com",
    phone: "(555) 987-6543",
    address: "10349 Courageous Drive, Indianapolis, IN 46236",
    propertyType: "Single Family Home",
    projectType: "Roof Replacement",
  };

  // Roof measurements - default values, will be overridden if we have a real job
  const [roofMeasurements, setRoofMeasurements] = useState({
    totalArea: 3880,
    pitchedArea: 3500,
    flatArea: 380,
    roofType: "Complex Gable",
    pitch: "6/12",
    squareFootage: 3880,
    starter: 240,
    caps: 135,
    valley: 75,
    ridges: 135,
    dripEdge: 285,
    perimeter: 285,
  });

  // Selected contractor - starts with demo data, but will be hydrated from real bid/installer when available
  const [selectedContractor, setSelectedContractor] = useState({
    id: null,
    contractor: "Elite Roofing Co.",
    contactName: "Mike Johnson",
    avatar:
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face",
    rating: 4.9,
    reviews: 127,
    phone: "(555) 123-4567",
    email: "mike@eliteroofing.com",
    address: "123 Oak St, Springfield, IL",
    selectedBid: {
      price: 10250,
      timeline: "3-5 days",
      warranty: "15 years",
      materials: "Premium Metal Shingles",
      estimatedCompletion: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
      included: [
        "All materials and labor",
        "Cleanup and debris removal",
        "Permit handling",
        "15-year workmanship warranty",
        "Emergency repair guarantee",
        "Post-installation inspection",
      ],
    },
  });

  // Hydrate from real job/bid when identifiers are present or from localStorage selection
  useEffect(() => {
    console.log(jobIdParam, "run112 ", jobIdParam);

    // 1) No explicit job/bid in URL: try to restore from localStorage
    if (!jobIdParam || !bidIdParam) {
      try {
        const rawIds = localStorage.getItem("rb_selected_bid");
        console.log("run 2 ", rawIds);

        const rawContractor = localStorage.getItem("rb_selected_contractor");
        if (!rawIds && !rawContractor) return;

        const parsedIds = rawIds ? JSON.parse(rawIds) : null;
        const parsedContractor = rawContractor
          ? JSON.parse(rawContractor)
          : null;

        console.log(parsedContractor, "parsedIds: ", parsedIds);

        // Prefer the full contractor payload we stored from CompareBidsPage
        if (parsedContractor) {
          setSelectedContractor((prev) => ({
            ...prev,
            id: parsedContractor.installerId || prev.id,
            contractor: parsedContractor.contractor || prev.contractor,
            contactName: parsedContractor.contactName || prev.contactName,
            avatar: parsedContractor.avatar || prev.avatar,
            rating: parsedContractor.rating ?? prev.rating,
            reviews: parsedContractor.reviews ?? prev.reviews,
            phone: parsedContractor.phone || prev.phone,
            email: parsedContractor.email || prev.email,
            address: parsedContractor.address || prev.address,
            completedJobs: parsedContractor.completedJobs ?? prev.completedJobs,
            selectedBid: {
              ...prev.selectedBid,
              price:
                parsedContractor.selectedBid?.price ?? prev.selectedBid?.price,
              timeline:
                parsedContractor.selectedBid?.timeline ??
                prev.selectedBid?.timeline,
              warranty:
                parsedContractor.selectedBid?.warranty ??
                prev.selectedBid?.warranty,
              materials:
                parsedContractor.selectedBid?.materials ??
                prev.selectedBid?.materials,
              included:
                parsedContractor.selectedBid?.included ||
                prev.selectedBid?.included,
            },
          }));

          // If we also know job/bid ids, hydrate roof measurements for that job
          if (parsedIds?.jobId) {
            const job = getJob(parsedIds.jobId);
            if (job) {
              setRoofMeasurements((prevRm) => ({
                ...prevRm,
                totalArea: job.squareFeet || prevRm.totalArea,
                pitchedArea:
                  job.pitchedArea || job.squareFeet || prevRm.pitchedArea,
                flatArea: job.flatArea || prevRm.flatArea,
                roofType: job.roofType || prevRm.roofType,
              }));
            }
          }

          return;
        }

        // Fallback to legacy ids-only flow
        if (!parsedIds?.jobId || !parsedIds?.bidId) return;

        const job = getJob(parsedIds.jobId);
        const bids = getBidsByJob(parsedIds.jobId);
        const bid = bids.find((b) => b.id === parsedIds.bidId);
        if (!job || !bid) return;

        const profile = getInstallerProfile(bid.installerId);

        setSelectedContractor((prev) => ({
          ...prev,
          id: bid.installerId,
          contractor: profile.companyName || prev.contractor,
          contactName: profile.contactName || prev.contactName,
          avatar: profile.avatar || prev.avatar,
          rating: profile.rating ?? prev.rating,
          reviews: profile.reviews ?? prev.reviews,
          phone: profile.phone || prev.phone,
          email: profile.email || prev.email,
          address: profile.address || prev.address,
          selectedBid: {
            ...prev.selectedBid,
            price: bid.amountTotal ?? prev.selectedBid?.price,
            timeline: profile.baseTimeline || `${bid.timelineDays || 5} days`,
            warranty: `${bid.warrantyYears || 10} years`,
            materials: bid.materials || prev.selectedBid?.materials,
            estimatedCompletion:
              prev.selectedBid?.estimatedCompletion ||
              new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
            included:
              profile.whatsIncluded && profile.whatsIncluded.length
                ? profile.whatsIncluded
                : prev.selectedBid?.included,
          },
        }));

        if (job) {
          setRoofMeasurements((prevRm) => ({
            ...prevRm,
            totalArea: job.squareFeet || prevRm.totalArea,
            pitchedArea:
              job.pitchedArea || job.squareFeet || prevRm.pitchedArea,
            flatArea: job.flatArea || prevRm.flatArea,
            roofType: job.roofType || prevRm.roofType,
          }));
        }
      } catch (e) {
        // ignore parse / ls errors, keep demo defaults
      }
      return;
    }

    // 2) Explicit job/bid provided via URL parameters
    try {
      const effectiveJobId = jobIdParam;
      const effectiveBidId = bidIdParam;
      if (!effectiveJobId || !effectiveBidId) return;

      const job = getJob(effectiveJobId);
      const bids = getBidsByJob(effectiveJobId);
      const bid = bids.find((b) => b.id === effectiveBidId);
      if (!job || !bid) return;

      const profile = getInstallerProfile(bid.installerId);

      setSelectedContractor((prev) => ({
        ...prev,
        id: bid.installerId,
        contractor: profile.companyName || prev.contractor,
        contactName: profile.contactName || prev.contactName,
        avatar: profile.avatar || prev.avatar,
        rating: profile.rating ?? prev.rating,
        reviews: profile.reviews ?? prev.reviews,
        phone: profile.phone || prev.phone,
        email: profile.email || prev.email,
        address: profile.address || prev.address,
        selectedBid: {
          ...prev.selectedBid,
          price: bid.amountTotal ?? prev.selectedBid?.price,
          timeline: profile.baseTimeline || `${bid.timelineDays || 5} days`,
          warranty: `${bid.warrantyYears || 10} years`,
          materials: bid.materials || prev.selectedBid?.materials,
          estimatedCompletion:
            prev.selectedBid?.estimatedCompletion ||
            new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
          included:
            profile.whatsIncluded && profile.whatsIncluded.length
              ? profile.whatsIncluded
              : prev.selectedBid?.included,
        },
      }));

      if (job) {
        setRoofMeasurements((prevRm) => ({
          ...prevRm,
          totalArea: job.squareFeet || prevRm.totalArea,
          pitchedArea: job.pitchedArea || job.squareFeet || prevRm.pitchedArea,
          flatArea: job.flatArea || prevRm.flatArea,
          roofType: job.roofType || prevRm.roofType,
        }));
      }
    } catch (e) {
      // swallow any client-side data issues, keep demo defaults
    }
  }, [jobIdParam, bidIdParam]);

  // Once we know installer id, try to hydrate contractor from Supabase `profiles`
  useEffect(() => {
    const loadFromSupabase = async () => {
      if (!supabase || !selectedContractor.id) return;
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", selectedContractor.id)
          .single();
        if (error || !data) return;

        setSelectedContractor((prev) => ({
          ...prev,
          contractor: data.company_name || prev.contractor,
          contactName: data.name || prev.contactName,
          avatar: data.avatar || prev.avatar,
          rating: data.rating ?? prev.rating,
          reviews: data.reviews ?? prev.reviews,
          phone: data.phone || prev.phone,
          email: data.email || prev.email,
          address: data.address || prev.address,
        }));
      } catch (e) {
        // Fail silently, keep existing local/demo data
        console.warn("Failed to hydrate selected contractor from Supabase", e);
      }
    };

    loadFromSupabase();
  }, [selectedContractor.id]);

  // Mock weather forecast
  const weatherForecast = [
    {
      date: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000),
      condition: "sunny",
      temp: "75°F",
      precipitation: "0%",
      icon: Sun,
    },
    {
      date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
      condition: "partly-cloudy",
      temp: "72°F",
      precipitation: "10%",
      icon: Cloud,
    },
    {
      date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
      condition: "sunny",
      temp: "78°F",
      precipitation: "5%",
      icon: Sun,
    },
    {
      date: new Date(Date.now() + 4 * 24 * 60 * 60 * 1000),
      condition: "cloudy",
      temp: "70°F",
      precipitation: "20%",
      icon: Cloud,
    },
    {
      date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
      condition: "rainy",
      temp: "68°F",
      precipitation: "80%",
      icon: CloudRain,
    },
  ];

  const steps = [
    { number: 1, title: "Schedule", icon: Calendar },
    { number: 2, title: "Details", icon: FileText },
    { number: 3, title: "Payment", icon: CreditCard },
    { number: 4, title: "Review", icon: CheckCircle },
    { number: 5, title: "Pay", icon: CreditCard },
  ];

  const handleInputChange = (field, value) => {
    setInstallationData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!installationData.preferredDate)
          newErrors.preferredDate = "Preferred date is required";
        if (!installationData.preferredTime)
          newErrors.preferredTime = "Preferred time is required";
        break;
      case 2:
        if (!installationData.contactMethod)
          newErrors.contactMethod = "Contact method is required";
        break;
      case 3:
        if (!installationData.paymentMethod)
          newErrors.paymentMethod = "Payment method is required";
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    // Check if user is logged in
    const { data } = await supabase.auth.getUser();
    const loggedInUser = data?.user;

    // 👉 If NOT logged in, save orderToBePlaced and redirect to login
    if (!loggedInUser) {
      const guestUserId = "guest-" + Date.now();

      const orderData = {
        user_id: guestUserId,
        user_email: homeownerDetails?.email || "guest@example.com",
        order_date: new Date().toISOString().split("T")[0], // ensures not-null
        status: "pending",

        homeowner_name: homeownerDetails?.name || "Guest",
        homeowner_email: homeownerDetails?.email || "guest@example.com",
        homeowner_phone: homeownerDetails?.phone || "N/A",
        property_address: homeownerDetails?.address || "N/A",
        property_type: homeownerDetails?.propertyType || "N/A",
        project_type: homeownerDetails?.projectType || "N/A",

        contractor_name: selectedContractor?.contractor || "N/A",
        contractor_contact: selectedContractor?.contactName || "N/A",
        contractor_phone: selectedContractor?.phone || "N/A",
        contractor_email: selectedContractor?.email || "N/A",
        contractor_address: selectedContractor?.address || "N/A",
        contractor_rating: selectedContractor?.rating || 0,
        contractor_avatar: selectedContractor?.avatar || null,

        total_cost: selectedContractor?.selectedBid?.price || 0,
        timeline: selectedContractor?.selectedBid?.timeline || 0,
        warranty: selectedContractor?.selectedBid?.warranty || "N/A",
        materials: selectedContractor?.selectedBid?.materials || "N/A",
        scheduled_date: installationData?.preferredDate || null,
        preferred_time: installationData?.preferredTime || null,
        alternate_date: installationData?.alternateDate || null,
        alternate_time: installationData?.alternateTime || null,
        completion_date: null,

        roof_measurements: roofMeasurements || [],

        contact_method: installationData?.contactMethod || "N/A",
        special_instructions: installationData?.specialInstructions || "",
        access_instructions: installationData?.accessInstructions || "",
        emergency_contact: installationData?.emergencyContact || "",
        payment_method: installationData?.paymentMethod || "N/A",

        bid_details: selectedContractor?.selectedBid || {},
      };

      localStorage.setItem("pending_order", JSON.stringify(orderData));
      localStorage.setItem("redirect_after_login", "/make-payment");
      navigate("/login");
      return;
    }

    // 👉 If logged in: continue processing payment normally
    setIsSubmitting(true);
    setIsProcessingPayment(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 3000));

      let orderToSave = null;
      const savedOrder = localStorage.getItem("pending_order");

      if (savedOrder) {
        orderToSave = JSON.parse(savedOrder);
        orderToSave.user_id = loggedInUser.id;
        localStorage.removeItem("pending_order");
        localStorage.removeItem("redirect_after_login");
      } else {
        // Fallback: fill all required fields if user places order directly
        orderToSave = {
          user_id: loggedInUser.id,
          user_email: homeownerDetails?.email || "guest@example.com",
          order_date: new Date().toISOString().split("T")[0],
          status: "pending",

          homeowner_name: homeownerDetails?.name || "Guest",
          homeowner_email: homeownerDetails?.email || "guest@example.com",
          homeowner_phone: homeownerDetails?.phone || "N/A",
          property_address: homeownerDetails?.address || "N/A",
          property_type: homeownerDetails?.propertyType || "N/A",
          project_type: homeownerDetails?.projectType || "N/A",

          contractor_name: selectedContractor?.contractor || "N/A",
          contractor_contact: selectedContractor?.contactName || "N/A",
          contractor_phone: selectedContractor?.phone || "N/A",
          contractor_email: selectedContractor?.email || "N/A",
          contractor_address: selectedContractor?.address || "N/A",
          contractor_rating: selectedContractor?.rating || 0,
          contractor_avatar: selectedContractor?.avatar || null,

          total_cost: selectedContractor?.selectedBid?.price || 0,
          timeline: selectedContractor?.selectedBid?.timeline || 0,
          warranty: selectedContractor?.selectedBid?.warranty || "N/A",
          materials: selectedContractor?.selectedBid?.materials || "N/A",
          scheduled_date: installationData?.preferredDate || null,
          preferred_time: installationData?.preferredTime || null,
          alternate_date: installationData?.alternateDate || null,
          alternate_time: installationData?.alternateTime || null,
          completion_date: null,

          roof_measurements: roofMeasurements || [],

          contact_method: installationData?.contactMethod || "N/A",
          special_instructions: installationData?.specialInstructions || "",
          access_instructions: installationData?.accessInstructions || "",
          emergency_contact: installationData?.emergencyContact || "",
          payment_method: installationData?.paymentMethod || "N/A",

          bid_details: selectedContractor?.selectedBid || {},
        };
      }

      const result = await supabaseHelpers.saveOrder(orderToSave);

      if (result.success) {
        setIsProcessingPayment(false);
        setCurrentStep(6);
      } else {
        console.error(result.error);
        alert("Failed to save order. Please try again.");
        setIsProcessingPayment(false);
      }
    } catch (error) {
      console.error("Error submitting installation:", error);
      alert("Something went wrong. Try again.");
      setIsProcessingPayment(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Print function for measurements and details
  const handlePrint = () => {
    window.print();
  };

  // Generate and download PDF
  const handleDownloadPDF = () => {
    try {
      const doc = generatePDF({
        homeownerDetails,
        roofMeasurements,
        selectedContractor,
        installationData,
        formatDate,
      });
      
      doc.save("RoofBidder-Installation-Order.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  // Step 6: Confirmation
  if (currentStep === 6) {
    return (
      <ConfirmationView
        selectedContractor={selectedContractor}
        installationData={installationData}
        roofMeasurements={roofMeasurements}
        homeownerDetails={homeownerDetails}
        formatDate={formatDate}
        handleDownloadPDF={handleDownloadPDF}
        handlePrint={handlePrint}
      />
    );
  }

  return (
    <>
      <RoofBidderHeader>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="max-w-6xl mx-auto px-4 py-8">
            {/* Progress Steps */}
            <div className="mb-12">
              <div className="flex items-center justify-center space-x-8">
                {steps.map((step, index) => {
                  const IconComponent = step.icon;
                  return (
                    <div key={step.number} className="flex items-center">
                      <div className={`flex items-center space-x-3`}>
                        <div
                          className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold transition-all duration-300 ${
                            currentStep >= step.number
                              ? "bg-emerald-600 text-white shadow-lg shadow-emerald-600/30"
                              : "bg-gray-200 text-gray-500"
                          }`}
                        >
                          <IconComponent className="w-6 h-6" />
                        </div>
                        <div className="hidden sm:block">
                          <div
                            className={`font-semibold text-sm ${
                              currentStep >= step.number
                                ? "text-emerald-600"
                                : "text-gray-500"
                            }`}
                          >
                            {step.title}
                          </div>
                        </div>
                      </div>
                      {index < steps.length - 1 && (
                        <div
                          className={`hidden sm:block w-20 h-0.5 mx-4 ${
                            currentStep > step.number
                              ? "bg-emerald-600"
                              : "bg-gray-200"
                          }`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-3xl shadow-lg p-8 border border-gray-100">
                  {/* Step 1: Schedule */}
                  {currentStep === 1 && (
                    <ScheduleStep
                      installationData={installationData}
                      handleInputChange={handleInputChange}
                      errors={errors}
                    />
                  )}

                  {/* Step 2: Details */}
                  {currentStep === 2 && (
                    <DetailsStep
                      installationData={installationData}
                      handleInputChange={handleInputChange}
                      errors={errors}
                    />
                  )}

                  {/* Step 3: Payment */}
                  {currentStep === 3 && (
                    <PaymentMethodStep
                      installationData={installationData}
                      handleInputChange={handleInputChange}
                      selectedContractor={selectedContractor}
                      errors={errors}
                    />
                  )}

                  {/* Step 4: Review */}
                  {currentStep === 4 && (
                    <ReviewStep
                      installationData={installationData}
                      formatDate={formatDate}
                    />
                  )}

                  {/* Step 5: Payment Processing */}
                  {currentStep === 5 && (
                    <PaymentProcessingStep
                      installationData={installationData}
                      selectedContractor={selectedContractor}
                      isProcessingPayment={isProcessingPayment}
                      paymentCardDetails={paymentCardDetails}
                      setPaymentCardDetails={setPaymentCardDetails}
                    />
                  )}
                </div>

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8">
                  <button
                    onClick={prevStep}
                    disabled={currentStep === 1 || isSubmitting}
                    className={`px-8 py-4 rounded-2xl font-semibold text-lg transition-all ${
                      currentStep === 1 || isSubmitting
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    Back
                  </button>

                  {currentStep === 5 ? (
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className={`px-10 py-4 rounded-2xl font-semibold text-lg text-white transition-all shadow-lg ${
                        isSubmitting
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 transform hover:scale-105 shadow-emerald-600/30"
                      }`}
                    >
                      {isSubmitting ? "Processing..." : "Confirm & Pay"}
                    </button>
                  ) : (
                    <button
                      onClick={nextStep}
                      className="px-10 py-4 rounded-2xl font-semibold text-lg text-white bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 transition-all transform hover:scale-105 shadow-lg shadow-emerald-600/30"
                    >
                      Continue
                    </button>
                  )}
                </div>
              </div>

              {/* Sidebar Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-3xl shadow-lg p-6 border border-gray-100 sticky top-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">
                    Order Summary
                  </h3>

                  {/* Contractor Info */}
                  <div className="flex items-center space-x-4 mb-6 pb-6 border-b border-gray-100">
                    <img
                      src={selectedContractor.avatar}
                      alt={selectedContractor.contractor}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-semibold text-gray-900">
                        {selectedContractor.contractor}
                      </div>
                      <div className="text-sm text-gray-500">
                        {selectedContractor.contactName}
                      </div>
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Project Type:</span>
                      <span className="font-medium text-gray-900">
                        Roof Replacement
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Roof Area:</span>
                      <span className="font-medium text-gray-900">
                        {roofMeasurements.totalArea.toLocaleString()} sq ft
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Materials:</span>
                      <span className="font-medium text-gray-900 text-right w-1/2">
                        {selectedContractor.selectedBid.materials}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Warranty:</span>
                      <span className="font-medium text-gray-900">
                        {selectedContractor.selectedBid.warranty}
                      </span>
                    </div>
                  </div>

                  {/* Total Price */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-6">
                    <div className="text-center">
                      <div className="text-sm text-gray-500 mb-1">
                        Total Estimated Cost
                      </div>
                      <div className="text-3xl font-bold text-emerald-600">
                        $
                        {selectedContractor.selectedBid.price.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {/* Weather Forecast */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <Cloud className="w-5 h-5 text-blue-500 mr-2" />
                      Installation Forecast
                    </h4>
                    <div className="space-y-3">
                      {weatherForecast.slice(0, 3).map((day, index) => {
                        const WeatherIcon = day.icon;
                        return (
                          <div
                            key={index}
                            className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center space-x-3">
                              <WeatherIcon className="w-5 h-5 text-gray-400" />
                              <span className="text-sm font-medium text-gray-700">
                                {day.date.toLocaleDateString("en-US", {
                                  weekday: "short",
                                })}
                              </span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className="text-sm font-bold text-gray-900">
                                {day.temp}
                              </span>
                              <span className="text-xs text-blue-500 bg-blue-50 px-2 py-1 rounded-full">
                                {day.precipitation} rain
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </RoofBidderHeader>
    </>
  );
};

export default ConfirmInstallationPage;
